import { useMemo } from "react"
import { NavLink, Outlet } from "react-router-dom"
import {
  User,
  MonitorSmartphone,
  Bell,
  Building2,
  Users,
  Plug,
  CreditCard,
  type LucideIcon,
} from "lucide-react"
import { cn } from "@/utils/Functions"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { PATH_ROOT } from "@/routes/paths"

type SettingsNavItem = {
  label: string
  segment: string
  icon: LucideIcon
}

type SettingsNavGroup = {
  label: string
  items: SettingsNavItem[]
}

const NAV_GROUPS: SettingsNavGroup[] = [
  {
    label: "Account",
    items: [
      { label: "Profile", segment: "profile", icon: User },
      { label: "Sessions", segment: "sessions", icon: MonitorSmartphone },
      { label: "Notifications", segment: "notifications", icon: Bell },
    ],
  },
  {
    label: "Organization",
    items: [
      { label: "General", segment: "general", icon: Building2 },
      { label: "Members", segment: "members", icon: Users },
      { label: "Integrations", segment: "integrations", icon: Plug },
      { label: "Plan and Pricing", segment: "plan", icon: CreditCard },
    ],
  },
]

function SettingsPage() {
  const isDemo = useIsDemo()
  const basePath = isDemo ? PATH_ROOT.demo.settings : PATH_ROOT.settings.root

  const groups = useMemo(() => NAV_GROUPS, [])

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="shrink-0 border-b border-border px-6 py-5 lg:px-8">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Settings
        </h1>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Internal settings navigation */}
        <nav
          aria-label="Settings"
          className="w-56 shrink-0 overflow-y-auto border-r border-border px-3 py-6"
        >
          {groups.map((group) => (
            <div key={group.label} className="mb-6 last:mb-0">
              <p className="px-3 pb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {group.label}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <li key={item.segment}>
                      <NavLink
                        to={`${basePath}/${item.segment}`}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-muted text-foreground"
                              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          )
                        }
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Active section */}
        <main className="min-h-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default SettingsPage
